import React, { useState } from 'react';
import { FiPlus, FiTrash2, FiEdit2, FiCheck, FiX, FiUpload, FiDownload } from 'react-icons/fi';

export default function QuestionsList({ questions, onChange }) {
  const [editingId, setEditingId] = useState(null);
  const [editText, setEditText] = useState('');
  const [newQuestion, setNewQuestion] = useState('');
  const [showImport, setShowImport] = useState(false);
  const [importText, setImportText] = useState('');

  function handleAdd() {
    if (!newQuestion.trim()) return;
    
    const maxId = questions.length > 0 
      ? Math.max(...questions.map(q => q.id || 0))
      : 0;

    const question = {
      id: maxId + 1,
      text: newQuestion.trim(),
      fontSize: 24,
      paid: true,
      packageId: 1
    };

    onChange([...questions, question]);
    setNewQuestion('');
  }

  function handleDelete(id) {
    if (!window.confirm('Excluir esta pergunta?')) return;
    onChange(questions.filter(q => q.id !== id));
  }

  function handleEdit(question) {
    setEditingId(question.id);
    setEditText(question.text);
  }

  function handleSaveEdit(id) {
    if (!editText.trim()) return;
    onChange(questions.map(q => 
      q.id === id ? { ...q, text: editText.trim() } : q
    ));
    setEditingId(null);
    setEditText('');
  }

  function handleCancelEdit() {
    setEditingId(null);
    setEditText('');
  }

  function handleImport() {
    try {
      const parsed = JSON.parse(importText);
      let questionsToImport = [];

      // Suporta vários formatos de JSON
      if (Array.isArray(parsed)) {
        questionsToImport = parsed;
      } else if (typeof parsed === 'object') {
        // Pega o primeiro array encontrado no objeto
        const firstKey = Object.keys(parsed)[0];
        if (Array.isArray(parsed[firstKey])) {
          questionsToImport = parsed[firstKey];
        }
      }

      if (questionsToImport.length === 0) {
        alert('Nenhuma pergunta encontrada no JSON');
        return;
      }

      // Renumera IDs para evitar conflitos
      const maxId = questions.length > 0 
        ? Math.max(...questions.map(q => q.id || 0))
        : 0;

      const normalized = questionsToImport.map((q, idx) => ({
        id: maxId + idx + 1,
        text: q.text || q.question || '',
        fontSize: q.fontSize || 24,
        paid: q.paid ?? q.free === false ?? true,
        packageId: q.packageId || 1
      }));

      onChange([...questions, ...normalized]);
      setImportText('');
      setShowImport(false);
      alert(`${normalized.length} perguntas importadas com sucesso!`);
    } catch (error) {
      alert('Erro ao importar JSON. Verifique o formato.');
      console.error(error);
    }
  }

  function handleExport() {
    const json = JSON.stringify(questions, null, 2);
    const blob = new Blob([json], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'perguntas.json';
    a.click();
    URL.revokeObjectURL(url);
  }

  return (
    <div style={styles.container}>
      {/* Toolbar */}
      <div style={styles.toolbar}>
        <button onClick={() => setShowImport(!showImport)} style={styles.btnLight}>
          <FiUpload size={16} /> Importar JSON
        </button>
        {questions.length > 0 && (
          <button onClick={handleExport} style={styles.btnLight}>
            <FiDownload size={16} /> Exportar JSON
          </button>
        )}
      </div>

      {/* Import Section */}
      {showImport && (
        <div style={styles.importBox}>
          <textarea
            placeholder='Cole o JSON aqui... Ex: [{"text": "Pergunta 1"}, {"text": "Pergunta 2"}]'
            value={importText}
            onChange={(e) => setImportText(e.target.value)}
            style={styles.importTextarea}
            rows={6}
          />
          <div style={styles.importActions}>
            <button onClick={() => setShowImport(false)} style={styles.btnCancel}>
              Cancelar
            </button>
            <button onClick={handleImport} style={styles.btnPrimary}>
              Importar
            </button>
          </div>
        </div>
      )}

      {/* Add New Question */}
      <div style={styles.addBox}>
        <input
          type="text"
          placeholder="Digite uma nova pergunta..."
          value={newQuestion}
          onChange={(e) => setNewQuestion(e.target.value)}
          onKeyPress={(e) => e.key === 'Enter' && handleAdd()}
          style={styles.addInput}
        />
        <button onClick={handleAdd} style={styles.btnAdd}>
          <FiPlus size={18} /> Adicionar
        </button>
      </div>

      {/* Questions List */}
      {questions.length === 0 ? (
        <div style={styles.empty}>
          Nenhuma pergunta adicionada ainda.
        </div>
      ) : (
        <div style={styles.list}>
          {questions.map((question, index) => (
            <div key={question.id || index} style={styles.questionItem}>
              <div style={styles.questionNumber}>{index + 1}</div>
              <div style={styles.questionContent}>
                {editingId === question.id ? (
                  <input
                    type="text"
                    value={editText}
                    onChange={(e) => setEditText(e.target.value)}
                    style={styles.editInput}
                    autoFocus
                  />
                ) : (
                  <div style={styles.questionText}>{question.text}</div>
                )}
              </div>
              <div style={styles.questionActions}>
                {editingId === question.id ? (
                  <>
                    <button
                      onClick={() => handleSaveEdit(question.id)}
                      style={styles.btnIcon}
                      title="Salvar"
                    >
                      <FiCheck size={16} color="#22863a" />
                    </button>
                    <button
                      onClick={handleCancelEdit}
                      style={styles.btnIcon}
                      title="Cancelar"
                    >
                      <FiX size={16} color="#d73a49" />
                    </button>
                  </>
                ) : (
                  <>
                    <button
                      onClick={() => handleEdit(question)}
                      style={styles.btnIcon}
                      title="Editar"
                    >
                      <FiEdit2 size={14} color="#1f6feb" />
                    </button>
                    <button
                      onClick={() => handleDelete(question.id)}
                      style={styles.btnIcon}
                      title="Excluir"
                    >
                      <FiTrash2 size={14} color="#d73a49" />
                    </button>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const styles = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
  },
  toolbar: {
    display: 'flex',
    gap: 8,
    flexWrap: 'wrap',
  },
  btnLight: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    padding: '8px 12px',
    background: '#f5f6fb',
    color: '#111',
    border: '1px solid #d9dbe7',
    borderRadius: 8,
    fontSize: 13,
    fontWeight: 600,
    cursor: 'pointer',
  },
  importBox: {
    background: '#fafbff',
    border: '1px solid #e8e9ef',
    borderRadius: 10,
    padding: 16,
  },
  importTextarea: {
    width: '100%',
    padding: 10,
    border: '1px solid #d9dbe7',
    borderRadius: 8,
    fontSize: 13,
    fontFamily: 'monospace',
    outline: 'none',
    resize: 'vertical',
    boxSizing: 'border-box',
  },
  importActions: {
    display: 'flex',
    gap: 8,
    justifyContent: 'flex-end',
    marginTop: 12,
  },
  btnCancel: {
    padding: '8px 14px',
    background: '#fff',
    color: '#111',
    border: '1px solid #d9dbe7',
    borderRadius: 8,
    fontSize: 13,
    fontWeight: 600,
    cursor: 'pointer',
  },
  btnPrimary: {
    padding: '8px 14px',
    background: '#1f6feb',
    color: '#fff',
    border: 'none',
    borderRadius: 8,
    fontSize: 13,
    fontWeight: 600,
    cursor: 'pointer',
  },
  addBox: {
    display: 'flex',
    gap: 8,
  },
  addInput: {
    flex: 1,
    padding: '10px 12px',
    border: '1px solid #d9dbe7',
    borderRadius: 10,
    fontSize: 14,
    outline: 'none',
  },
  btnAdd: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    padding: '10px 16px',
    background: '#1f6feb',
    color: '#fff',
    border: 'none',
    borderRadius: 10,
    fontSize: 14,
    fontWeight: 600,
    cursor: 'pointer',
    whiteSpace: 'nowrap',
  },
  empty: {
    padding: 40,
    textAlign: 'center',
    color: '#999',
    background: '#fafafa',
    border: '1px dashed #e0e2ea',
    borderRadius: 10,
  },
  list: {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
  },
  questionItem: {
    display: 'flex',
    gap: 12,
    padding: 12,
    background: '#fafbff',
    border: '1px solid #e8e9ef',
    borderRadius: 10,
    alignItems: 'center',
  },
  questionNumber: {
    minWidth: 32,
    height: 32,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: '#1f6feb',
    color: '#fff',
    borderRadius: 8,
    fontSize: 13,
    fontWeight: 700,
  },
  questionContent: {
    flex: 1,
  },
  questionText: {
    fontSize: 14,
    lineHeight: 1.5,
    color: '#111',
  },
  editInput: {
    width: '100%',
    padding: '8px 10px',
    border: '1px solid #1f6feb',
    borderRadius: 8,
    fontSize: 14,
    outline: 'none',
  },
  questionActions: {
    display: 'flex',
    gap: 6,
  },
  btnIcon: {
    width: 32,
    height: 32,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: '#fff',
    border: '1px solid #d9dbe7',
    borderRadius: 8,
    cursor: 'pointer',
  },
};
